import { useCallback, useEffect } from 'react';
import { EditorView } from '@codemirror/view';
import { useUI, useEditorState } from '../contexts';

type SearchMatch = {
  from: number;
  to: number;
  line: number;
};

export function useSearch(editorViewRef: React.MutableRefObject<EditorView | null>) {
  const {
    searchPanelOpen,
    setSearchPanelOpen,
    searchQuery,
    searchOptions,
    setSearchMatches,
    currentMatchIndex,
    setCurrentMatchIndex,
    searchInputRef,
  } = useUI();

  const { code } = useEditorState();

  // Build regex from search options
  const buildSearchRegex = useCallback(
    (query: string) => {
      if (!query) return null;
      try {
        let pattern = query;
        if (!searchOptions.regex) {
          pattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
        if (searchOptions.wholeWord) {
          pattern = `\\b${pattern}\\b`;
        }
        const flags = searchOptions.caseSensitive ? 'g' : 'gi';
        return new RegExp(pattern, flags);
      } catch {
        return null;
      }
    },
    [searchOptions]
  );

  // Find all matches in code
  const findMatches = useCallback(
    (query: string, text: string): SearchMatch[] => {
      const regex = buildSearchRegex(query);
      if (!regex) return [];

      const matches: SearchMatch[] = [];
      let match;
      while ((match = regex.exec(text)) !== null) {
        const from = match.index;
        const to = from + match[0].length;
        const line = text.slice(0, from).split('\n').length;
        matches.push({ from, to, line });
        if (match[0].length === 0) regex.lastIndex++;
      }
      return matches;
    },
    [buildSearchRegex]
  );

  // Update matches when search query or code changes
  useEffect(() => {
    if (!searchPanelOpen || !searchQuery) {
      setSearchMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    const matches = findMatches(searchQuery, code);
    setSearchMatches(matches);
    if (matches.length > 0 && currentMatchIndex >= matches.length) {
      setCurrentMatchIndex(0);
    }
  }, [searchQuery, code, searchPanelOpen, searchOptions, findMatches, setSearchMatches, currentMatchIndex, setCurrentMatchIndex]);

  // Highlight current match in editor
  const highlightMatch = useCallback(
    (index: number, matches: SearchMatch[]) => {
      const view = editorViewRef.current;
      if (!view || matches.length === 0 || index < 0 || index >= matches.length) return;

      const match = matches[index];
      view.dispatch({
        selection: { anchor: match.from, head: match.to },
        scrollIntoView: true,
      });
    },
    [editorViewRef]
  );

  // Navigate to next match
  const findNext = useCallback(() => {
    const matches = findMatches(searchQuery, code);
    if (matches.length === 0) return;

    const nextIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIndex);
    highlightMatch(nextIndex, matches);
  }, [searchQuery, code, currentMatchIndex, findMatches, setCurrentMatchIndex, highlightMatch]);

  // Navigate to previous match
  const findPrevious = useCallback(() => {
    const matches = findMatches(searchQuery, code);
    if (matches.length === 0) return;

    const prevIndex = currentMatchIndex === 0 ? matches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    highlightMatch(prevIndex, matches);
  }, [searchQuery, code, currentMatchIndex, findMatches, setCurrentMatchIndex, highlightMatch]);

  // Replace current match
  const replaceOne = useCallback(
    (replaceText: string) => {
      const view = editorViewRef.current;
      const matches = findMatches(searchQuery, code);
      if (!view || matches.length === 0) return;

      const match = matches[currentMatchIndex];
      view.dispatch({
        changes: { from: match.from, to: match.to, insert: replaceText },
      });
    },
    [editorViewRef, searchQuery, code, currentMatchIndex, findMatches]
  );

  // Replace all matches
  const replaceAll = useCallback(
    (replaceText: string) => {
      const view = editorViewRef.current;
      const matches = findMatches(searchQuery, code);
      if (!view || matches.length === 0) return;

      // Replace from end to start to preserve positions
      const changes = [...matches]
        .reverse()
        .map((match) => ({ from: match.from, to: match.to, insert: replaceText }));

      view.dispatch({ changes });
      setCurrentMatchIndex(0);
    },
    [editorViewRef, searchQuery, code, findMatches, setCurrentMatchIndex]
  );

  // Open search panel
  const openSearch = useCallback(() => {
    setSearchPanelOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.select();
      }
    }, 50);
  }, [setSearchPanelOpen, searchInputRef]);

  // Close search panel
  const closeSearch = useCallback(() => {
    setSearchPanelOpen(false);
  }, [setSearchPanelOpen]);

  return {
    openSearch,
    closeSearch,
    findNext,
    findPrevious,
    replaceOne,
    replaceAll,
  };
}
